import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ModalWindow } from '../GeneralComponents/ModalWindow/ModalWindow'
import FeelsSyncConfirmation from './FeelsSyncConfirmation'
import errorToast from '../Toasts/ErrorToast'
import successToast from '../Toasts/SuccessToast'
import { useAPI } from '../../Contexts/APIContext'
import classes from './FeelsSyncButton.module.css'

const FeelsSyncButton = ({ song, onSyncComplete, onSyncStatusChange, onBeforeSync, hasUnsavedEdits }) => {
  const { runFeelsSync, runQuickTime, fetchLyricsToEdit } = useAPI()
  const [menuOpen, setMenuOpen] = useState(false)
  const storageKey = `feelssync_${song?.f_id}`
  const isSyncedForVariant = (data) => data?.f_sync_run === true
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [syncStatus, setSyncStatus] = useState(() => {
    if (isSyncedForVariant(song)) {
      // Clear any stale localStorage data
      if (song?.f_id) {
        localStorage.removeItem(storageKey)
      }
      return 'idle' // Show normal state when sync is complete
    }

    // Check localStorage for existing sync status
    const stored = localStorage.getItem(storageKey)
    return stored ? JSON.parse(stored).status : 'idle'
  })
  const [syncTimer, setSyncTimer] = useState(null)
  const [timeoutTimer, setTimeoutTimer] = useState(null)

  const TIMEOUT_DURATION = 20 * 60 * 1000 // 20 minutes for production

  const storeSyncStatus = (status) => {
    if (song?.f_id) {
      localStorage.setItem(storageKey, JSON.stringify({
        status,
        timestamp: Date.now(),
        songName: song.name,
        artistName: song.artist?.name
      }))
    }
  }

  const clearSyncStatus = () => {
    if (song?.f_id) {
      localStorage.removeItem(storageKey)
    }
  }

  const handleTimeout = () => {
    setSyncTimer(null)
    setTimeoutTimer(null)
    setSyncStatus('error')
    storeSyncStatus('error')
    errorToast('FeelsSync timed out after 20 minutes. Please check the correctness of the lyrics, remove any unusual characters, and try again.')
  }

  const startPolling = (remainingTimeout = TIMEOUT_DURATION) => {
    const interval = setInterval(() => {
      fetchLyricsToEdit(
        song.f_id,
        (data) => {
          data.json().then((lyricsData) => {
            if (isSyncedForVariant(lyricsData)) {
              clearInterval(interval)
              clearTimeout(timeout)
              setSyncTimer(null)
              setTimeoutTimer(null)

              // Check the current state to determine if it succeeded and has lyrics
              const hasLyrics = lyricsData.has_lyrics === true
              const hasLyricsSync = lyricsData.lyrics_sync && lyricsData.lyrics_sync.length > 0

              // Determine if this was a transcription attempt (original song had no lyrics)
              const wasTranscriptionAttempt = song?.has_lyrics === false

              if (wasTranscriptionAttempt && !hasLyrics && !hasLyricsSync) {
                // Transcription failed - f_sync_run is true but no lyrics were produced
                setSyncStatus('error')
                storeSyncStatus('error')
                errorToast('Transcription failed. The song returned with no lyrics. Please add lyrics manually using "Edit Lyrics" and try FeelsSync for timing.')
              } else {
                // Success - transcription/sync produced results
                setSyncStatus('success')
                clearSyncStatus()
                const message = wasTranscriptionAttempt
                  ? `Transcription completed on ${song.name} by ${song.artist.name}. Please review the lyrics for accuracy and fine-tune the timing.`
                  : `FeelsSync completed on ${song.name} by ${song.artist.name}. You can now review and fine-tune the timing.`
                successToast(message)
              }

              if (onSyncComplete) {
                onSyncComplete({ ...song, lyrics_sync: lyricsData.lyrics_sync, f_sync_run: true, has_lyrics: lyricsData.has_lyrics, is_public_visible: lyricsData.is_public_visible, timing_source: lyricsData.timing_source, timing_flags: lyricsData.timing_flags, check_lyrics: lyricsData.check_lyrics, timed_at: lyricsData.timed_at, is_sync_verified: lyricsData.is_sync_verified })
              }
            }
          })
        },
        (error) => {
          console.error('Failed to check FeelsSync status:', error)
        }
      )
    }, 60 * 1000)

    const timeout = setTimeout(() => {
      clearInterval(interval)
      handleTimeout()
    }, remainingTimeout)

    setSyncTimer(interval)
    setTimeoutTimer(timeout)
  }

  const handleButtonClick = () => {
    setIsConfirmationOpen(true)
  }

  // Right-click power menu (only when the button is actionable).
  const handleContextMenu = (e) => {
    if (getButtonState() !== 'enabled') return
    e.preventDefault()
    setMenuOpen(true)
  }

  // QuickTime: synchronous MMS-only retime, no flags. Blocks (warm ~2s, cold
  // worker up to ~3 min) then swaps in the timing — same completion handler
  // as a normal FeelsSync.
  const handleQuickTime = async () => {
    setMenuOpen(false)

    if (onBeforeSync) {
      try {
        await onBeforeSync()
      } catch (err) {
        errorToast('Could not save your edits — QuickTime aborted. Fix the save error and try again.')
        return
      }
    }

    setSyncStatus('pending')
    storeSyncStatus('pending')

    runQuickTime(
      song.f_id,
      (data) => {
        data.json().then((songData) => {
          setSyncStatus('success')
          clearSyncStatus()
          successToast(`QuickTime finished on ${song.name}${song.artist?.name ? ` by ${song.artist.name}` : ''}. Review and fine-tune the timing.`)
          if (onSyncComplete) {
            onSyncComplete({ ...song, lyrics_sync: songData.lyrics_sync, f_sync_run: true, has_lyrics: songData.has_lyrics, is_public_visible: songData.is_public_visible, timing_source: songData.timing_source, timing_flags: songData.timing_flags, check_lyrics: songData.check_lyrics, timed_at: songData.timed_at, is_sync_verified: songData.is_sync_verified })
          }
        })
      },
      () => {
        setSyncStatus('error')
        storeSyncStatus('error')
        errorToast('QuickTime could not complete. The timing worker may be warming up (try again in a moment), or this song has no lyrics to time.')
      }
    )
  }

  const handleConfirmSync = async () => {
    setIsConfirmationOpen(false)

    // Auto-save any pending lyric edits first so the backend retimes the
    // user's latest text, not whatever was last persisted. If the save
    // fails, abort sync — the FeelsSync request would otherwise quietly
    // run against stale lyrics AND silently overwrite the unsaved edits
    // when the synced data comes back.
    if (onBeforeSync) {
      try {
        await onBeforeSync()
      } catch (err) {
        errorToast('Could not save your edits — FeelsSync aborted. Fix the save error and try again.')
        return
      }
    }

    setSyncStatus('pending')
    storeSyncStatus('pending')

    runFeelsSync(
      song.f_id,
      (data) => {
        data.json().then(() => {
          successToast('Added to FeelsSync queue...')
          startPolling()
        })
      },
      (error) => {
        console.error('FeelsSync error:', error)
        setSyncStatus('error')
        storeSyncStatus('error')
        const errorMessage = song?.has_lyrics === false
          ? 'Transcription could not be started. This feature is currently unavailable for this song. The Feels team is looking into it for you.'
          : 'FeelsSync could not be completed. This feature is currently unavailable for this song. The Feels team is looking into it for you.'
        errorToast(errorMessage)
      }
    )
  }

  const handleCancelSync = () => {
    setIsConfirmationOpen(false)
  }


  React.useEffect(() => {
    // Restore polling if page was refreshed during pending sync
    if (syncStatus === 'pending' && song?.f_id && !syncTimer) {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const { timestamp } = JSON.parse(stored)
        const elapsed = Date.now() - timestamp

        // If less than timeout duration has passed, continue polling
        if (elapsed < TIMEOUT_DURATION) {
          const remainingTime = TIMEOUT_DURATION - elapsed
          startPolling(remainingTime)
        } else {
          // Time already expired
          handleTimeout()
        }
      }
    }
  }, [song?.f_id, syncStatus, syncTimer])


  React.useEffect(() => {
    // Notify parent of sync status changes
    if (onSyncStatusChange) {
      onSyncStatusChange(syncStatus)
    }
  }, [syncStatus, onSyncStatusChange])

  React.useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      if (syncTimer) {
        clearInterval(syncTimer)
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer)
      }
    }
  }, [syncTimer, timeoutTimer])

  const getButtonState = () => {
    const isSyncInProgress = ['pending', 'success', 'error'].includes(syncStatus)

    // If no lyrics, only disable if sync is in progress
    if (song?.has_lyrics === false) {
      return isSyncInProgress ? 'disabled' : 'enabled'
    }

    // With lyrics, also check f_sync_run and is_sync_verified
    const isDisabled = isSyncInProgress ||
                      song?.f_sync_run === true ||
                      song?.is_sync_verified === true
    return isDisabled ? 'disabled' : 'enabled'
  }

  const getTooltipText = () => {
    if (syncStatus === 'pending') {
      return 'FeelsSync is in progress... This process may take up to 20 minutes. All retiming controls are disabled during this time.'
    }
    if (syncStatus === 'success') {
      return 'FeelsSync completed successfully. Lyrics have been updated with new timing.'
    }
    if (syncStatus === 'error') {
      return 'FeelsSync failed or timed out. Edit and save the lyrics to try again.'
    }
    if (song?.has_lyrics === false) {
      return 'We\'ll attempt to transcribe the lyrics, but please check for accuracy. You can add accurate lyrics by clicking "Edit Lyrics" first and send them out for timing.'
    }
    if (song?.is_sync_verified === true) {
      return 'Sync verified songs cannot be retimed with FeelsSync.'
    }
    if (song?.f_sync_run === true) {
      return 'FeelsSync has already been run on this song. Make manual timing adjustments if needed.'
    }
    return 'FeelsSync automatically retimes your content. This process can take between 5-20 minutes. Manual adjustments may still be required.'
  }

  const renderStatusIcon = () => {
    if (isSyncedForVariant(song)) {
      return (
        <div className={classes.successIcon}>
          ✓
        </div>
      )
    }
    
    switch (syncStatus) {
      case 'pending':
        return (
          <div className={classes.loadingIcon}>
            <div className={classes.spinner}></div>
          </div>
        )
      case 'success':
        return (
          <div className={classes.successIcon}>
            ✓
          </div>
        )
      case 'error':
        return (
          <div className={classes.errorIcon}>
            ✕
          </div>
        )
      default:
        return null
    }
  }

  return (
    <>
      <ModalWindow
        open={isConfirmationOpen}
        onClose={handleCancelSync}
        disableCloseIcon={true}
      >
        <FeelsSyncConfirmation
          onConfirm={handleConfirmSync}
          onCancel={handleCancelSync}
          hasLyrics={song?.has_lyrics}
          hasUnsavedEdits={hasUnsavedEdits}
        />
      </ModalWindow>

      <div className={classes.container}>
        <div className={classes.buttonWrapper}>
          <button
            className={classes.feelssyncBtn}
            onClick={handleButtonClick}
            onContextMenu={handleContextMenu}
            disabled={getButtonState() === 'disabled'}
          >
            {song?.has_lyrics === false && syncStatus === 'pending' ? 'TRANSCRIBING' : song?.has_lyrics === false ? 'TRANSCRIBE' : 'FEELSSYNC'}
          </button>
          <div className={classes.tooltip}>
            {getTooltipText()}
          </div>
          {menuOpen && (
            <>
              <div className={classes.menuBackdrop} onClick={() => setMenuOpen(false)} />
              <div className={classes.contextMenu}>
                <button className={classes.contextItem} onClick={handleQuickTime}>
                  QuickTime
                  <span className={classes.contextSub}>fastest, no checks</span>
                </button>
              </div>
            </>
          )}
        </div>
        {renderStatusIcon()}
      </div>
    </>
  )
}

FeelsSyncButton.propTypes = {
  song: PropTypes.shape({
    f_id: PropTypes.string.isRequired,
    name: PropTypes.string,
    artist: PropTypes.object,
    lyrics_sync: PropTypes.array,
    f_sync_run: PropTypes.bool,
    is_sync_verified: PropTypes.bool,
    has_lyrics: PropTypes.bool,
    timing_source: PropTypes.string,
  }).isRequired,
  onSyncComplete: PropTypes.func,
  onSyncStatusChange: PropTypes.func,
  onBeforeSync: PropTypes.func,
  hasUnsavedEdits: PropTypes.bool,
}

export default FeelsSyncButton